/**
 * Button props builders for ConfirmModal
 * Extracted to reduce component size
 */

import React from "react";
import { EButtonstyletype, EButtonVariant } from "../../Button";
import type { ConfirmModalProps } from "../ConfirmModal";
import { getDefaultButtonVariant, isConfirmButtonDisabled } from "./confirmModalButtonHelpers";
import { COMMON_UI_I18N } from "@Front/Components/DesignSystem/helpers/commonUiI18n";

export interface BuildConfirmButtonPropsParams {
	confirmButton?: ConfirmModalProps["confirmButton"];
	onConfirm?: ConfirmModalProps["onConfirm"];
	variant: "danger" | "warning" | "info";
	confirmDisabled: boolean;
	loading: boolean;
	handleConfirm: () => void | Promise<void>;
}

/**
 * Builds confirm button props
 */
export function buildConfirmButtonProps(params: BuildConfirmButtonPropsParams): {
	children: string;
	onClick: () => void | Promise<void>;
	variant: EButtonVariant;
	styletype: EButtonstyletype;
	disabled: boolean;
	fullwidth: boolean;
	leftIcon?: React.ReactNode;
	rightIcon?: React.ReactNode;
} | undefined {
	const { confirmButton, onConfirm, variant, confirmDisabled, loading, handleConfirm } = params;
	const defaultVariant = getDefaultButtonVariant(variant);

	if (confirmButton) {
		return {
			children: confirmButton.label,
			onClick: handleConfirm,
			variant: confirmButton.variant ?? defaultVariant,
			styletype: confirmButton.styleType ?? EButtonstyletype.CONTAINED,
			disabled: confirmDisabled || loading || (confirmButton.loading ?? false),
			fullwidth: confirmButton.fullwidth ?? true,
			leftIcon: confirmButton.leftIcon,
			rightIcon: confirmButton.rightIcon,
		};
	}

	if (onConfirm) {
		return {
			children: COMMON_UI_I18N.modal.confirmCta,
			onClick: handleConfirm,
			variant: defaultVariant,
			styletype: EButtonstyletype.CONTAINED,
			disabled: confirmDisabled || loading,
			fullwidth: true,
		};
	}

	return undefined;
}

export interface BuildConfirmModalDerivedPropsParams {
	variant: "danger" | "warning" | "info";
	requireConfirmation?: ConfirmModalProps["requireConfirmation"];
	confirmationValue: string;
	loading: boolean;
	confirmButton?: ConfirmModalProps["confirmButton"];
	onConfirm?: () => void | Promise<void>;
	handleConfirm: () => void | Promise<void>;
	cancelButton?: ConfirmModalProps["cancelButton"];
	handleCancel: () => void;
	thirdButton?: ConfirmModalProps["thirdButton"];
}

export function buildConfirmModalDerivedProps(params: BuildConfirmModalDerivedPropsParams): {
	confirmDisabled: boolean;
	confirmButtonProps: ReturnType<typeof buildConfirmButtonProps>;
	cancelButtonProps: ReturnType<typeof buildCancelButtonProps>;
	thirdButtonProps: ReturnType<typeof buildThirdButtonProps>;
} {
	const confirmDisabled =
		params.confirmButton?.disabled ||
		isConfirmButtonDisabled({
			variant: params.variant,
			requireConfirmation: params.requireConfirmation,
			confirmationValue: params.confirmationValue,
			loading: params.loading,
		});
	return {
		confirmDisabled,
		confirmButtonProps: buildConfirmButtonProps({
			confirmButton: params.confirmButton,
			onConfirm: params.onConfirm,
			variant: params.variant,
			confirmDisabled,
			loading: params.loading,
			handleConfirm: params.handleConfirm,
		}),
		cancelButtonProps: buildCancelButtonProps({ cancelButton: params.cancelButton, handleCancel: params.handleCancel }),
		thirdButtonProps: buildThirdButtonProps({ thirdButton: params.thirdButton }),
	};
}

export interface BuildCancelButtonPropsParams {
	cancelButton?: ConfirmModalProps["cancelButton"];
	handleCancel: () => void;
}

/**
 * Builds cancel button props
 */
export function buildCancelButtonProps(params: BuildCancelButtonPropsParams): {
	children: string;
	onClick: () => void;
	variant: EButtonVariant;
	styletype: EButtonstyletype;
	fullwidth: boolean;
} {
	const { cancelButton, handleCancel } = params;

	if (cancelButton) {
		return {
			children: cancelButton.label ?? COMMON_UI_I18N.modal.cancelCta,
			onClick: handleCancel,
			variant: cancelButton.variant ?? EButtonVariant.SECONDARY,
			styletype: cancelButton.styleType ?? EButtonstyletype.TEXT,
			fullwidth: cancelButton.fullwidth ?? true,
		};
	}

	return {
		children: COMMON_UI_I18N.modal.cancelCta,
		onClick: handleCancel,
		variant: EButtonVariant.SECONDARY,
		styletype: EButtonstyletype.TEXT,
		fullwidth: true,
	};
}

export interface BuildThirdButtonPropsParams {
	thirdButton?: ConfirmModalProps["thirdButton"];
}

/**
 * Builds third button props
 */
export function buildThirdButtonProps(params: BuildThirdButtonPropsParams): {
	children: string | undefined;
	onClick: (() => void) | undefined;
	variant: EButtonVariant;
	styletype: EButtonstyletype;
	fullwidth: boolean;
} | undefined {
	const { thirdButton } = params;

	if (thirdButton) {
		return {
			children: thirdButton.label,
			onClick: thirdButton.onClick,
			variant: thirdButton.variant ?? EButtonVariant.SECONDARY,
			styletype: thirdButton.styleType ?? EButtonstyletype.OUTLINED,
			fullwidth: thirdButton.fullwidth ?? true,
		};
	}

	return undefined;
}
